// v2H — Admin Audit log with filters (admin user, action, target, date range, sensitive-only) and a detail diff drawer.
import React, { useEffect, useMemo, useState } from "react";
import { ShieldAlert, Filter, X, Calendar, User, FileText, AlertTriangle } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const COLOR_BY_TYPE = (t) => {
    if (t?.includes("approve") || t?.includes("dispatch") || t?.includes("delivered")) return "bg-emerald-100 text-emerald-700";
    if (t?.includes("reject") || t?.includes("cancel") || t?.includes("disable") || t?.includes("delete")) return "bg-rose-100 text-rose-600";
    if (t?.includes("refund") || t?.includes("restock") || t?.includes("packed")) return "bg-blue-100 text-blue-700";
    if (t?.includes("role")) return "bg-brand-100 text-brand-700";
    return "bg-slate-100 text-slate-600";
};

const DiffDrawer = ({ entry, onClose }) => {
    if (!entry) return null;
    const meta = entry.metadata || {};
    const before = meta.before;
    const after = meta.after;
    const otherKeys = Object.keys(meta).filter((k) => k !== "before" && k !== "after");
    return (
        <div className="fixed inset-0 z-[80] flex justify-end bg-black/30" onClick={onClose} data-testid="audit-diff-drawer">
            <div className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <header className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-100 bg-white px-5 py-3">
                    <div>
                        <p className="label-uppercase">Audit detail</p>
                        <h2 className="text-base font-semibold tracking-tight font-mono">{entry.action_type}</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100" data-testid="audit-diff-close"><X className="h-4 w-4" /></button>
                </header>
                <div className="space-y-5 p-5 text-sm">
                    {entry.is_sensitive && (
                        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-800">
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                            <div>
                                <p className="text-xs font-semibold">Security-sensitive action</p>
                                <p className="text-[11px]">This action is automatically flagged for compliance review.</p>
                            </div>
                        </div>
                    )}
                    <section>
                        <p className="label-uppercase">Actor</p>
                        <p className="mt-1 text-sm font-medium">{entry.admin_email}</p>
                        <p className="text-[11px] uppercase tracking-wider text-zinc-400">{entry.admin_role} · {entry.admin_user_id?.slice(0, 8)}</p>
                    </section>
                    <section>
                        <p className="label-uppercase">Target</p>
                        <p className="mt-1 text-sm font-medium">{entry.target_type} · <span className="font-mono text-xs text-zinc-500">{entry.target_id}</span></p>
                    </section>
                    <section>
                        <p className="label-uppercase">When</p>
                        <p className="mt-1 text-sm">{new Date(entry.created_at).toLocaleString("en-IN")}</p>
                    </section>
                    {(before || after) && (
                        <section>
                            <p className="label-uppercase">Diff</p>
                            <div className="mt-2 grid gap-2 md:grid-cols-2">
                                <div className="rounded-md border border-rose-200 bg-rose-50/60 p-3 text-[11px]">
                                    <p className="mb-1 font-semibold text-rose-700">Before</p>
                                    <pre className="whitespace-pre-wrap break-all font-mono">{JSON.stringify(before ?? {}, null, 2)}</pre>
                                </div>
                                <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-[11px]">
                                    <p className="mb-1 font-semibold text-emerald-700">After</p>
                                    <pre className="whitespace-pre-wrap break-all font-mono">{JSON.stringify(after ?? {}, null, 2)}</pre>
                                </div>
                            </div>
                        </section>
                    )}
                    {otherKeys.length > 0 && (
                        <section>
                            <p className="label-uppercase">Metadata</p>
                            <table className="mt-2 w-full text-xs">
                                <tbody className="divide-y divide-zinc-100">
                                    {otherKeys.map((k) => (
                                        <tr key={k}>
                                            <td className="py-1.5 pr-3 text-zinc-500">{k}</td>
                                            <td className="py-1.5 font-mono text-zinc-800 break-all">{typeof meta[k] === "object" ? JSON.stringify(meta[k]) : String(meta[k])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

const Content = () => {
    const [filters, setFilters] = useState({ action_type: "", admin_user_id: "", target_type: "", from: "", to: "", sensitive_only: false });
    const [admins, setAdmins] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    // Load filterable admin list for the dropdown
    useEffect(() => {
        api.get("/admin/users", { params: { role: "admin", page_size: 50 } })
            .then((r) => setAdmins(r.data.items || []))
            .catch(() => setAdmins([]));
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = { page, page_size: 50 };
            if (filters.action_type) params.action_type = filters.action_type;
            if (filters.admin_user_id) params.admin_user_id = filters.admin_user_id;
            if (filters.target_type) params.target_type = filters.target_type;
            if (filters.from) params.from = filters.from;
            if (filters.to) params.to = filters.to;
            if (filters.sensitive_only) params.sensitive_only = true;
            const r = await api.get("/admin/audit", { params });
            setRows(r.data.items);
            setTotal(r.data.total);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, page]);

    const allTypes = useMemo(() => Array.from(new Set(rows.map((r) => r.action_type))).sort(), [rows]);
    const allTargetTypes = useMemo(() => Array.from(new Set(rows.map((r) => r.target_type).filter(Boolean))).sort(), [rows]);

    const resetFilters = () => setFilters({ action_type: "", admin_user_id: "", target_type: "", from: "", to: "", sensitive_only: false });
    const activeCount = Object.values(filters).filter((v) => v && v !== false).length;

    return (
        <div className="space-y-5" data-testid="admin-audit-page">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
                <div className="flex flex-wrap items-end gap-3">
                    <label className="block">
                        <span className="label-uppercase">Action type</span>
                        <select value={filters.action_type} onChange={(e) => { setFilters({ ...filters, action_type: e.target.value }); setPage(1); }} className="mt-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="audit-type-filter">
                            <option value="">All actions</option>
                            {allTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="label-uppercase">Admin</span>
                        <select value={filters.admin_user_id} onChange={(e) => { setFilters({ ...filters, admin_user_id: e.target.value }); setPage(1); }} className="mt-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="audit-admin-filter">
                            <option value="">All admins</option>
                            {admins.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="label-uppercase">Target type</span>
                        <select value={filters.target_type} onChange={(e) => { setFilters({ ...filters, target_type: e.target.value }); setPage(1); }} className="mt-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="audit-target-filter">
                            <option value="">All targets</option>
                            {allTargetTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="label-uppercase">From</span>
                        <input type="date" value={filters.from} onChange={(e) => { setFilters({ ...filters, from: e.target.value }); setPage(1); }} className="mt-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="audit-from-filter" />
                    </label>
                    <label className="block">
                        <span className="label-uppercase">To</span>
                        <input type="date" value={filters.to} onChange={(e) => { setFilters({ ...filters, to: e.target.value }); setPage(1); }} className="mt-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="audit-to-filter" />
                    </label>
                    <button
                        onClick={() => { setFilters({ ...filters, sensitive_only: !filters.sensitive_only }); setPage(1); }}
                        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${filters.sensitive_only ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                        data-testid="audit-sensitive-toggle"
                    >
                        <ShieldAlert className="h-3 w-3" /> Sensitive only
                    </button>
                    {activeCount > 0 && (
                        <button onClick={resetFilters} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600" data-testid="audit-reset-filters">
                            <X className="h-3 w-3" /> Clear ({activeCount})
                        </button>
                    )}
                    <p className="ml-auto text-xs text-slate-500">{total} entries</p>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-4 py-3 text-left">When</th>
                            <th className="px-4 py-3 text-left">Admin</th>
                            <th className="px-4 py-3 text-left">Action</th>
                            <th className="px-4 py-3 text-left">Target</th>
                            <th className="px-4 py-3 text-left">Metadata</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? <tr><td colSpan={6} className="px-4 py-6"><div className="h-6 skeleton rounded" /></td></tr> :
                          rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="audit-empty">No entries match.</td></tr> :
                          rows.map((r) => (
                            <tr key={r.id} className="hover:bg-slate-50" data-testid={`audit-row-${r.id}`}>
                                <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                                <td className="px-4 py-2.5">
                                    <p className="text-xs font-medium text-slate-900">{r.admin_email}</p>
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400">{r.admin_role}</p>
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${COLOR_BY_TYPE(r.action_type)}`}>{r.action_type}</span>
                                    {r.is_sensitive && (
                                        <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700" title="Security-sensitive">
                                            <ShieldAlert className="h-2.5 w-2.5" /> SENS
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-slate-700">{r.target_type} · <span className="font-mono text-slate-500">{r.target_id?.slice(0, 8)}…</span></td>
                                <td className="px-4 py-2.5 text-xs text-slate-600">
                                    {Object.keys(r.metadata || {}).slice(0, 3).map((k) => (
                                        <span key={k} className="mr-2 inline-block">
                                            <span className="text-slate-400">{k}:</span> {typeof r.metadata[k] === "object" ? "{…}" : String(r.metadata[k]).slice(0, 40)}
                                        </span>
                                    ))}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    <button onClick={() => setSelected(r)} className="text-xs font-medium text-brand-700 hover:underline" data-testid={`audit-view-${r.id}`}>View</button>
                                </td>
                            </tr>
                          ))}
                    </tbody>
                </table>
                {!loading && total > 50 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>Page {page} of {Math.ceil(total / 50)}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 50 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>

            <DiffDrawer entry={selected} onClose={() => setSelected(null)} />
        </div>
    );
};

const AdminAudit = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Audit log" subtitle="Every state-changing admin action, append-only · sensitive actions flagged">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminAudit;
